import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, Shield, X, Settings, Check } from "lucide-react";
import { Button } from "./ui/button";
import { Link } from "react-router-dom";

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const cookieConsent = localStorage.getItem("cookie_consent");
    if (!cookieConsent) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptAll = () => {
    const consent = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem("cookie_consent", JSON.stringify(consent));
    setIsVisible(false);
  };

  const acceptNecessaryOnly = () => {
    const consent = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem("cookie_consent", JSON.stringify(consent));
    setIsVisible(false);
  };

  const savePreferences = () => {
    const consent = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem("cookie_consent", JSON.stringify(consent));
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
      >
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
          {/* Main Banner */}
          {!showDetails ? (
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-violet-500 to-indigo-600 rounded-xl flex items-center justify-center">
                  <Cookie className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    🍪 Nous utilisons des cookies
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-4">
                    Nous utilisons des cookies pour améliorer votre expérience sur notre site, 
                    analyser le trafic et personnaliser le contenu. Vous pouvez accepter tous les cookies 
                    ou personnaliser vos préférences.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      onClick={acceptAll}
                      className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Tout accepter
                    </Button>
                    <Button
                      onClick={acceptNecessaryOnly}
                      variant="outline"
                      className="border-slate-300"
                    >
                      Refuser les optionnels
                    </Button>
                    <Button
                      onClick={() => setShowDetails(true)}
                      variant="ghost"
                      className="text-slate-600"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Personnaliser
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-3">
                    En savoir plus dans notre{" "}
                    <Link to="/legal#cookies" className="text-violet-600 hover:underline">
                      Politique de cookies
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          ) : (
            /* Detailed Preferences */
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-violet-600" />
                  Paramètres des cookies
                </h3>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                {/* Necessary Cookies */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <h4 className="font-medium text-slate-900">Cookies essentiels</h4>
                    <p className="text-sm text-slate-600">
                      Nécessaires au fonctionnement du site (authentification, sécurité)
                    </p>
                  </div>
                  <div className="flex items-center">
                    <span className="text-xs text-slate-500 mr-2">Toujours actifs</span>
                    <div className="w-12 h-6 bg-violet-600 rounded-full relative">
                      <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full" />
                    </div>
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <h4 className="font-medium text-slate-900">Cookies analytiques</h4>
                    <p className="text-sm text-slate-600">
                      Nous aident à comprendre comment vous utilisez le site
                    </p>
                  </div>
                  <button
                    onClick={() => setPreferences(p => ({ ...p, analytics: !p.analytics }))}
                    className={`w-12 h-6 rounded-full relative transition-colors ${
                      preferences.analytics ? "bg-violet-600" : "bg-slate-300"
                    }`}
                  >
                    <div
                      className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${
                        preferences.analytics ? "right-1" : "left-1"
                      }`}
                    />
                  </button>
                </div>

                {/* Marketing Cookies */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <h4 className="font-medium text-slate-900">Cookies marketing</h4>
                    <p className="text-sm text-slate-600">
                      Utilisés pour vous proposer des contenus personnalisés
                    </p>
                  </div>
                  <button
                    onClick={() => setPreferences(p => ({ ...p, marketing: !p.marketing }))}
                    className={`w-12 h-6 rounded-full relative transition-colors ${
                      preferences.marketing ? "bg-violet-600" : "bg-slate-300"
                    }`}
                  >
                    <div
                      className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${
                        preferences.marketing ? "right-1" : "left-1"
                      }`}
                    />
                  </button>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={savePreferences}
                  className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white"
                >
                  Enregistrer mes préférences
                </Button>
                <Button
                  onClick={acceptAll}
                  variant="outline"
                >
                  Tout accepter
                </Button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieBanner;
